import { MouseEventHandler, PropsWithChildren } from 'react';
import { usePushGtmEvent } from '../generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '../generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { TooltipReadMoreLink, TooltipReadMoreLinkProps } from '../generic/components/tooltips/common-styles';
import { BouLinkExternal, ExternalLinkProps } from '../generic/content/texts/bou-link';

export enum KBUrl {
  INDEX = 'https://docs.uprightplatform.com',
  BACKGROUND_NET_IMPACT = 'https://docs.uprightplatform.com/background/why-net-impact',
  METRICS_NET_IMPACT = 'https://docs.uprightplatform.com/metrics/net-impact',
  METRICS_SDGS = 'https://docs.uprightplatform.com/metrics/un-sdg-alignment',
  METRICS_TAXONOMY = 'https://docs.uprightplatform.com/metrics/eu-taxonomy',
  METRICS_PAIS = 'https://docs.uprightplatform.com/metrics/sfdr-pai-indicators',
  METHODOLOGY_NET_IMPACT = 'https://docs.uprightplatform.com/methodology/net-impact',
  METHODOLOGY_SDGS = 'https://docs.uprightplatform.com/methodology/sdg-alignment',
  METHODOLOGY_SDGS_AGGREGATES = 'https://docs.uprightplatform.com/methodology/sdg-alignment#aggregate-alignments-for-sdg-targets',
  METHODOLOGY_TAXONOMY = 'https://docs.uprightplatform.com/methodology/eu-taxonomy',
  METHODOLOGY_PAIS = 'https://docs.uprightplatform.com/methodology/sfdr-pai-indicators',
  NET_IMPACT_RATIO = 'https://docs.uprightplatform.com/metrics/net-impact#net-impact-ratio-nir',
  MONETIZATION = 'https://docs.uprightplatform.com/methodology/net-impact/weighting-of-impacts',
  RELEASE_CYCLE = 'https://docs.uprightplatform.com/releases/release-cycle',
  /** Step-by-step setup for connecting Upright's MCP server to an external AI client. */
  AGENT_EXTERNAL_CLIENTS_MCP = 'https://docs.uprightplatform.com/upright-agent/external-clients-mcp',
  ATTRIBUTION = 'https://docs.uprightplatform.com/methodology/net-impact/overview-of-the-upright-net-impact-model/allocation-of-impact-across-value-chains',
  ATTRIBUTION_ILLUSTRATIVE_GHG_EXAMPLE = 'https://docs.uprightplatform.com/appendix/illustrative-example-of-attribute-only-once',
  // TODO should this be removed?
  OPEN_ACCESS = 'https://docs.uprightplatform.com/background/open-access-to-upright-data',
  INDICATIVE_GUIDELINES_FOR_CLASSIFYING_INVESTMENS_IN_LINE_WITH_SFDR = 'https://docs.uprightplatform.com/appendix/indicative-guidelines-for-classifying-investments-in-line-with-sfdr#applying-upright-data',
}

type KBLinkProps = {
  href: KBUrl;
} & Omit<ExternalLinkProps, 'href'>;
export function KBLink(props: PropsWithChildren<KBLinkProps>) {
  const pushGtmEvent = usePushGtmEvent();
  const onClick: MouseEventHandler<HTMLAnchorElement> = event => {
    pushGtmEvent({
      event: CustomGtmEvent.KB_LINK_CLICK,
      data: {
        url: props.href,
      },
    });
    if (props.onClick) {
      props.onClick(event);
    }
  };
  return (
    <BouLinkExternal {...props} href={props.href} onClick={onClick}>
      {props.children}
    </BouLinkExternal>
  );
}

type TooltipReadMoreKBLinkProps = {
  href: KBUrl;
} & Omit<TooltipReadMoreLinkProps, 'href'>;
export function TooltipReadMoreKbLink(props: TooltipReadMoreKBLinkProps) {
  const pushGtmEvent = usePushGtmEvent();
  const onClick = () => {
    pushGtmEvent({
      event: CustomGtmEvent.KB_LINK_CLICK,
      data: {
        url: props.href,
      },
    });
    if (props.onClick) {
      props.onClick();
    }
  };
  return <TooltipReadMoreLink {...{ props }} href={props.href} onClick={onClick} />;
}

export function useOnKbLinkClick() {
  const pushGtmEvent = usePushGtmEvent();
  return (url: KBUrl) => {
    pushGtmEvent({
      event: CustomGtmEvent.KB_LINK_CLICK,
      data: {
        url,
      },
    });
  };
}
